import { useState, useEffect, useCallback, type ReactNode } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronsUpDown, Plus, Search, SlidersHorizontal, Trash2 } from "lucide-react";
import { apiClient, apiDelete, apiGet } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { translateApiError } from "@/lib/errors/error-map";
import { useAuthStore } from "@/lib/auth/store";
import { CategoryMultiSelect } from "@/features/categories/category-multi-select";
import { flattenWithDepth, type Category } from "@/features/categories/types";
import { ProductStockHistoryModal } from "./product-stock-history-modal";

// Khớp productListItemResponse của BE (camelCase). Trước đây FE đọc snake_case
// (is_active, variants[]) không tồn tại trong JSON → mọi SP đều hiện "Ẩn",
// variants 0, giá "—".
interface ProductRow {
  id: string;
  name: string;
  brand?: string | null;
  categoryId?: string | null;
  isActive: boolean;
  variantCount: number;
  stockQty: number;
  sellMin?: string | null;
  sellMax?: string | null;
}

interface ProductPage {
  data: ProductRow[];
  meta: { total: number; limit: number; offset: number };
}

// Bộ lọc tìm nâng cao — AND với nhau, gửi thẳng làm query param.
interface AdvFilter {
  sku: string;
  brand: string;
  name: string;
  status: "" | "true" | "false";
  sellMin: string;
  sellMax: string;
  costMin: string;
  costMax: string;
}

const EMPTY_ADV: AdvFilter = {
  sku: "",
  brand: "",
  name: "",
  status: "",
  sellMin: "",
  sellMax: "",
  costMin: "",
  costMax: "",
};

function fmtVND(n: number) {
  return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(n);
}

// Clickable column header: click to sort by this column, click again to flip.
// (Same pattern as stock-list-page SortTh.)
function SortTh({
  col,
  sortBy,
  sortDir,
  onSort,
  align,
  children,
}: {
  col: string;
  sortBy: string;
  sortDir: "asc" | "desc";
  onSort: (c: string) => void;
  align?: "center" | "right";
  children: ReactNode;
}) {
  const active = sortBy === col;
  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
  return (
    <Th className={align === "right" ? "text-right" : align === "center" ? "text-center" : ""}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""} hover:text-foreground ${active ? "font-semibold text-foreground" : "text-muted-foreground"}`}
      >
        {children}
        <Icon size={13} className={active ? "opacity-100" : "opacity-40"} />
      </button>
    </Th>
  );
}

export function ProductsListPage() {
  const user = useAuthStore((s) => s.user);
  const canMutate = user?.role === "admin" || user?.role === "manager" || user?.role === "sales";

  // ?category=<id> deep-link từ trang Danh mục → lọc sẵn danh mục đó.
  const urlSearch = useSearch({ strict: false }) as { category?: string };
  const [search, setSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [categoryIds, setCategoryIds] = useState<string[]>(
    urlSearch.category ? [urlSearch.category] : [],
  );
  const [advOpen, setAdvOpen] = useState(false);
  const [adv, setAdv] = useState<AdvFilter>(EMPTY_ADV);
  const [debouncedAdv, setDebouncedAdv] = useState<AdvFilter>(EMPTY_ADV);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  // Sắp xếp theo cột — "" = mặc định (mới nhất trước).
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  // Modal lịch sử xuất nhập kho — mở khi bấm số Tồn.
  const [historyFor, setHistoryFor] = useState<{ id: string; name: string } | null>(null);

  function toggleSort(col: string) {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
    setPage(1);
  }

  // Debounce ô tìm + panel nâng cao chung một nhịp.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(search);
      setDebouncedAdv(adv);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search, adv]);

  const { data: cats } = useQuery({
    queryKey: ["categories"],
    queryFn: () => apiGet<Category[]>("/v1/categories"),
  });

  // MỘT endpoint duy nhất (/v1/products) cho cả tìm thường + nâng cao — trước
  // đây tìm kiếm rẽ sang /products/search (shape khác, không phân trang).
  const { data: productPage, isLoading } = useQuery({
    queryKey: ["products", "list", page, pageSize, categoryIds, debouncedQ, debouncedAdv, sortBy, sortDir],
    queryFn: async () => {
      const a = debouncedAdv;
      const res = await apiClient.get<ProductPage>("/v1/products", {
        params: {
          limit: pageSize,
          offset: (page - 1) * pageSize,
          ...(sortBy ? { sort: sortBy, dir: sortDir } : {}),
          ...(debouncedQ.trim() ? { q: debouncedQ.trim() } : {}),
          ...(categoryIds.length ? { category_ids: categoryIds.join(",") } : {}),
          ...(a.sku.trim() ? { sku: a.sku.trim() } : {}),
          ...(a.brand.trim() ? { brand: a.brand.trim() } : {}),
          ...(a.name.trim() ? { name: a.name.trim() } : {}),
          ...(a.status ? { is_active: a.status } : {}),
          ...(a.sellMin ? { sell_min: a.sellMin } : {}),
          ...(a.sellMax ? { sell_max: a.sellMax } : {}),
          ...(a.costMin ? { cost_min: a.costMin } : {}),
          ...(a.costMax ? { cost_max: a.costMax } : {}),
        },
      });
      return res.data;
    },
  });

  const products = productPage?.data ?? [];
  const total = productPage?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  const catMap = Object.fromEntries(
    flattenWithDepth(cats ?? []).map(({ cat }) => [cat.id, cat.name]),
  );

  const handleCatChange = useCallback((v: string[]) => {
    setCategoryIds(v);
    setPage(1);
  }, []);

  const setAdvField = useCallback(<K extends keyof AdvFilter>(k: K, v: AdvFilter[K]) => {
    setAdv((prev) => ({ ...prev, [k]: v }));
  }, []);

  const hasAdv = Object.values(adv).some((v) => v !== "");

  // Soft-delete (is_active=FALSE) — keeps sales history. Manager/admin only.
  // BE giờ CHẶN xóa khi còn tồn (guardProductSellable) — hiện đúng thông báo lỗi.
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: (id: string) => apiDelete(`/v1/products/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] });
      toast.success("Đã xóa sản phẩm");
    },
    onError: (e) => toast.error(translateApiError(e)),
  });

  const handleDelete = useCallback(
    (id: string, name: string) => {
      if (window.confirm(`Xóa sản phẩm "${name}"?`)) del.mutate(id);
    },
    [del],
  );

  function priceRange(p: ProductRow) {
    const min = parseFloat(p.sellMin ?? "");
    const max = parseFloat(p.sellMax ?? "");
    if (isNaN(min) && isNaN(max)) return "—";
    if (isNaN(max) || min === max) return fmtVND(min);
    return `${fmtVND(min)} – ${fmtVND(max)}`;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Sản phẩm</h1>
        {canMutate && (
          <Link
            to="/products/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={16} />
            Thêm sản phẩm
          </Link>
        )}
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Tìm tên, thương hiệu, SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <CategoryMultiSelect
          values={categoryIds}
          onChange={handleCatChange}
          className="w-[240px]"
        />
        <Button
          variant={advOpen || hasAdv ? "default" : "outline"}
          onClick={() => setAdvOpen((o) => !o)}
          className="gap-1.5 shrink-0"
        >
          <SlidersHorizontal size={15} />
          Tìm nâng cao
        </Button>
      </div>

      {advOpen && (
        <div className="rounded-md border bg-muted/30 p-4 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Tên sản phẩm</label>
              <Input
                placeholder="VD: Redmi Note 15"
                value={adv.name}
                onChange={(e) => setAdvField("name", e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">SKU / Mã vạch</label>
              <Input
                placeholder="VD: 2000214…"
                value={adv.sku}
                onChange={(e) => setAdvField("sku", e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Thương hiệu</label>
              <Input
                placeholder="VD: Samsung"
                value={adv.brand}
                onChange={(e) => setAdvField("brand", e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Trạng thái</label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={adv.status}
                onChange={(e) => setAdvField("status", e.target.value as AdvFilter["status"])}
              >
                <option value="">Tất cả</option>
                <option value="true">Hoạt động</option>
                <option value="false">Ẩn</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Giá bán từ</label>
              <MoneyInput placeholder="0" value={adv.sellMin} onChange={(v) => setAdvField("sellMin", v)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Giá bán đến</label>
              <MoneyInput placeholder="Không giới hạn" value={adv.sellMax} onChange={(v) => setAdvField("sellMax", v)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Giá nhập từ</label>
              <MoneyInput placeholder="0" value={adv.costMin} onChange={(v) => setAdvField("costMin", v)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Giá nhập đến</label>
              <MoneyInput placeholder="Không giới hạn" value={adv.costMax} onChange={(v) => setAdvField("costMax", v)} />
            </div>
          </div>
          {hasAdv && (
            <Button variant="outline" size="sm" onClick={() => setAdv(EMPTY_ADV)}>
              Xóa lọc
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Đang tải…</p>
      ) : (
        <>
          <Table>
            <THead>
              <Tr>
                {/* Tên · Variants · Tồn · Giá bán đứng đầu (Sếp yêu cầu 28/07 —
                    mobile cuộn ngang thấy thông tin bán hàng trước tiên). */}
                <SortTh col="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Tên sản phẩm</SortTh>
                <SortTh col="variants" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="center">Variants</SortTh>
                <SortTh col="stock" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="center">Tồn</SortTh>
                <SortTh col="sell" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Giá bán</SortTh>
                <SortTh col="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Trạng thái</SortTh>
                <SortTh col="brand" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Thương hiệu</SortTh>
                <Th>Danh mục</Th>
                {canMutate && <Th className="text-right">Thao tác</Th>}
              </Tr>
            </THead>
            <TBody>
              {products.length === 0 && (
                <Tr>
                  <Td colSpan={canMutate ? 8 : 7} className="text-center text-muted-foreground">
                    Không có sản phẩm
                  </Td>
                </Tr>
              )}
              {products.map((p) => (
                <Tr key={p.id}>
                  <Td>
                    <Link
                      to="/products/$id"
                      params={{ id: p.id }}
                      className="font-medium hover:underline"
                    >
                      {p.name}
                    </Link>
                  </Td>
                  <Td className="text-center">{p.variantCount}</Td>
                  <Td className="text-center">
                    {/* Bấm số tồn → lịch sử xuất nhập chuyển kho của SP */}
                    <button
                      type="button"
                      onClick={() => setHistoryFor({ id: p.id, name: p.name })}
                      className={`font-mono font-medium hover:underline ${
                        p.stockQty > 0 ? "text-primary" : p.stockQty < 0 ? "text-red-600" : "text-muted-foreground"
                      }`}
                      title="Xem lịch sử xuất nhập kho"
                    >
                      {p.stockQty}
                    </button>
                  </Td>
                  <Td>{priceRange(p)}</Td>
                  <Td>
                    <span
                      className={
                        p.isActive
                          ? "rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700"
                          : "rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                      }
                    >
                      {p.isActive ? "Hoạt động" : "Ẩn"}
                    </span>
                  </Td>
                  <Td>{p.brand || "—"}</Td>
                  <Td>{(p.categoryId && catMap[p.categoryId]) || "—"}</Td>
                  {canMutate && (
                    <Td className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(p.id, p.name)}
                        disabled={del.isPending}
                        aria-label={`Xóa sản phẩm ${p.name}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </Td>
                  )}
                </Tr>
              ))}
            </TBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {total} sản phẩm — trang {page}/{totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Trước
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Sau
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {historyFor && (
        <ProductStockHistoryModal
          productId={historyFor.id}
          productName={historyFor.name}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}
